import { useEffect, useState } from 'react'
import { Avatar, Button, Card } from '@heroui/react'
import { api } from '../lib/api'
import type { Branch, Identity } from '../lib/types'
import { Chrome } from '../ui/Chrome'
import { Icon } from '../ui/icons'
import { roleLabel } from '../lib/roleNames'
import { useStaffText } from '../ui/StaffText'
import { initials } from '../lib/initials'
import { AccountTokens } from './accessTokens'
import '../styles/account.css'

/**
 * A person's own account: who the server thinks they are, the way to their
 * password, and their access tokens. The language is chosen only from the top bar.
 */
export function Account({
  identity,
  onNavigate,
  onSignOut,
}: {
  identity: Identity
  onNavigate: (to: string) => void
  onSignOut: () => void
}) {
  const { lang, say } = useStaffText()
  const staff = identity.staff
  const fromDirectory = staff.signIn === 'directory'
  const [branches, setBranches] = useState<Branch[]>([])

  // Only to name the branches: a failure leaves the row saying how many.
  useEffect(() => {
    api
      .branches()
      .then(({ branches }) => setBranches(branches))
      .catch(() => setBranches([]))
  }, [])

  const ids = staff.branchIds ?? []
  const branchText =
    staff.owner
      ? say('people.wholeServer')
      : ids.length === 0
        ? say('admin.everyBranch')
        : branches.length === 0
          ? say('admin.people.nBranches', { n: ids.length })
          : ids.map((id) => branches.find((b) => b.id === id)?.name ?? '—').join(', ')

  const role = staff.owner ? say('common.role.superadmin') : roleLabel(staff.roleKey, staff.roleName, lang)

  return (
    <Chrome title={say('entry.accountTitle')}>
      <main className="page account">
        <header className="account-head">
          <Avatar size="lg" color="accent" className="account-avatar" aria-hidden="true">
            <Avatar.Fallback>{initials(staff.displayName, staff.username)}</Avatar.Fallback>
          </Avatar>
          <div className="account-head__text">
            <h1 className="page__title account-name">{staff.displayName}</h1>
            <p className="account-user">{staff.username}</p>
          </div>
          <Button variant="secondary" onPress={onSignOut}>
            <Icon name="signout" size={16} /> {say('common.signOut')}
          </Button>
        </header>

        <Card className="account-card">
          <dl className="account-rows">
            <div className="account-row">
              <dt>{say('admin.people.role')}</dt>
              <dd>
                {role}
                {fromDirectory && <span className="account-row__note">{say('entry.setByDirectory')}</span>}
              </dd>
            </div>
            {identity.organisation && (
              <div className="account-row">
                <dt>{say('common.organisation')}</dt>
                <dd>{identity.organisation}</dd>
              </div>
            )}
            <div className="account-row">
              <dt>{say('admin.section.branches')}</dt>
              <dd>{branchText}</dd>
            </div>
            <div className="account-row">
              <dt>{say('people.col.signIn')}</dt>
              <dd>{fromDirectory ? say('people.directory') : say('people.password')}</dd>
              {!fromDirectory && (
                <Button variant="secondary" size="sm" onPress={() => onNavigate('/password')}>
                  <Icon name="lock" size={16} /> {say('entry.changeIt')}
                </Button>
              )}
            </div>
          </dl>
        </Card>

        <AccountTokens />
      </main>
    </Chrome>
  )
}
